import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, CreditCard, Loader2 } from 'lucide-react';
import { useCreatePayCheckout } from '@/hooks/useClinicPay';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';

export function PayInvoice() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const createCheckout = useCreatePayCheckout();

  const paymentStatus = searchParams.get('status');
  const isSucceeded = paymentStatus === 'succeeded';
  const isCancelled = paymentStatus === 'cancelled';

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  const [syncError, setSyncError] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    if (!token || isCancelled) {
      setInfoLoading(false);
      return;
    }
    if (token.length < 8) {
      setTokenInvalid(true);
      setInfoLoading(false);
      return;
    }

    let cancelled = false;
    async function syncPaymentStatus() {
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('pay-checkout-status', {
          body: { pay_token: token },
        });
        if (cancelled) return;

        if (invokeError) {
          console.error('pay checkout sync failed', invokeError);
          if (isSucceeded) setSyncError(true);
          return;
        }

        const result = data as { ok?: boolean; data?: { status?: string } } | null;
        if (result?.ok && result.data?.status === 'succeeded') {
          setPaymentComplete(true);
        } else if (isSucceeded) {
          setSyncError(true);
        }
      } catch (err) {
        console.error('pay checkout sync failed', err);
        if (!cancelled && isSucceeded) setSyncError(true);
      } finally {
        if (!cancelled) setInfoLoading(false);
      }
    }

    void syncPaymentStatus();
    return () => {
      cancelled = true;
    };
  }, [token, isSucceeded, isCancelled]);

  async function handlePay() {
    if (!token) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await createCheckout.mutateAsync({ pay_token: token });
      window.location.href = result.checkout_url;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('expired') || msg.includes('gone')) {
        setError(t('payments.expired'));
      } else if (msg.includes('already been paid') || msg.includes('already paid')) {
        setError(t('payments.already_paid'));
      } else {
        setError(t('payments.pay_error'));
      }
      setIsLoading(false);
    }
  }

  const showThanks = isSucceeded || paymentComplete;

  if (infoLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (showThanks) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-page-light px-4 dark:bg-page-dark">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <CheckCircle className="mx-auto mb-4 h-14 w-14 text-emerald-500" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('payments.thanks')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('payments.thanks_subtitle')}
          </p>
          {syncError && (
            <p className="mt-3 text-xs text-yellow-700 dark:text-yellow-400">
              {t('payments.sync_delayed')}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (tokenInvalid || !token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-page-light px-4 dark:bg-page-dark">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-card-dark">
          <p className="text-sm text-red-600 dark:text-red-400">{t('payments.expired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-page-light px-4 dark:bg-page-dark">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-card-dark">
        <div className="rounded-t-xl bg-indigo-600 px-6 py-4">
          <span className="text-lg font-bold text-white">CLINIO</span>
        </div>

        <div className="p-8">
          <h1 className="mb-1 text-xl font-semibold text-gray-900 dark:text-white">
            {t('payments.public_pay_title')}
          </h1>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            {t('payments.public_pay_subtitle')}
          </p>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {error}
            </div>
          )}

          {isCancelled && (
            <div className="mb-4 rounded-md bg-yellow-50 px-4 py-3 text-sm text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              {t('payments.payment_cancelled')}
            </div>
          )}

          <Button size="full" onClick={handlePay} disabled={isLoading || createCheckout.isPending}>
            {isLoading || createCheckout.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-4 w-4" />
                {t('payments.pay_now')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
